import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { StorageService } from '../services/StorageService';

const { FiCheckCircle, FiCircle, FiDownload, FiTrash2, FiBookOpen, FiHeart, FiEdit3, FiFeather } = FiIcons;

const Dashboard = ({ lessonProgress, updateLessonProgress }) => {
  const navigate = useNavigate();
  
  const lessons = [
    { number: 1, title: 'The Manuscript Readiness Audit', description: 'Assess your manuscript\'s true state' },
    { number: 2, title: 'Editing for the Reader\'s Experience', description: 'Craft a meaningful reading journey' },
    { number: 3, title: 'The Self-Editing System', description: 'Simplify your editing process' },
    { number: 4, title: 'Scene, Sentence, and Soul', description: 'Deepen your writing voice' },
    { number: 5, title: 'Publishing Pathways Simplified', description: 'Choose your publishing path' },
    { number: 6, title: 'Book Setup Basics', description: 'Prepare your book for upload' },
    { number: 7, title: 'Your Simple Launch Plan', description: 'Plan a faith-centered launch' },
    { number: 8, title: 'Stewarding Your Story Beyond Launch', description: 'Sustain your message long-term' }
  ];

  const handleExportAll = () => {
    const allData = StorageService.getAllReflections();
    const exportText = StorageService.formatForExport(allData);
    const blob = new Blob([exportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'refine-release-reflections.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all your data? This cannot be undone.')) {
      StorageService.clearAllData();
      window.location.reload();
    }
  };

  const completedLessons = Object.values(lessonProgress).filter(Boolean).length;

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="text-center py-12 px-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-soft paper-texture"
      >
        <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-medium">
          <FiFeather className="text-white text-2xl" />
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-primary mb-6 leading-tight">
          Welcome to Your Journey
        </h1>
        <p className="font-sans text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Welcome to your guided workspace for the Write. Refine. Release. Cohort. Each week, a new mini-lesson will unlock below. Use this app to reflect, record notes, and track your journey toward becoming a GospelWise author.
        </p>
      </motion.div>

      {/* Progress Overview */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-soft p-8 paper-texture"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBookOpen} className="text-primary text-xl" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-primary font-semibold">Your Progress</h2>
              <p className="font-sans text-sm text-gray-500 mt-1">Track your creative stewardship journey</p>
            </div>
          </div>
          <div className="text-center">
            <p className="font-serif text-3xl font-bold text-primary">{completedLessons}/8</p>
            <p className="font-sans text-sm text-gray-600">Lessons Complete</p>
          </div>
        </div>
        
        <div className="relative">
          <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
            <motion.div 
              className="bg-gradient-to-r from-primary to-accent h-4 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(completedLessons / 8) * 100}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </div>
        </div>
        
        <p className="font-sans text-sm text-gray-600 mt-4 italic">
          "Click a lesson tab to begin. Your notes are stored locally and can be exported anytime."
        </p>
      </motion.div>

      {/* Lesson Grid */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-soft p-8 paper-texture"
      >
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiHeart} className="text-accent text-xl" />
          </div>
          <div>
            <h2 className="font-serif text-2xl text-primary font-semibold">Lesson Journey</h2>
            <p className="font-sans text-sm text-gray-500 mt-1">Eight steps to refine and release your story</p>
          </div>
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          {lessons.map((lesson, index) => (
            <motion.div
              key={lesson.number}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              className="group cursor-pointer"
              onClick={() => navigate(`/lesson/${lesson.number}`)}
            >
              <div className="flex items-start gap-4 p-5 rounded-xl border border-gray-200 hover:border-primary/30 hover:shadow-soft transition-all duration-300 bg-white">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateLessonProgress(lesson.number, !lessonProgress[lesson.number]);
                  }}
                  className="text-xl hover:scale-110 transition-transform mt-1"
                >
                  <SafeIcon 
                    icon={lessonProgress[lesson.number] ? FiCheckCircle : FiCircle} 
                    className={lessonProgress[lesson.number] ? 'text-green-500' : 'text-gray-300'} 
                  />
                </button>
                <div className="flex-1">
                  <h3 className="font-serif font-semibold text-gray-800 group-hover:text-primary transition-colors mb-1">
                    {lesson.number}. {lesson.title}
                  </h3>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed">{lesson.description}</p>
                </div>
                <SafeIcon icon={FiIcons.FiChevronRight} className="text-gray-300 group-hover:text-primary transition-colors mt-2" />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.4 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <button 
          onClick={handleExportAll} 
          className="flex-1 bg-primary text-white px-8 py-4 rounded-xl font-sans font-semibold hover:bg-blue-700 transition-all duration-300 flex items-center justify-center gap-3 btn-hover shadow-soft"
        >
          <SafeIcon icon={FiDownload} className="text-xl" />
          <span>Export All Reflections</span>
        </button>
        <button 
          onClick={handleClearData} 
          className="flex-1 bg-white text-gray-700 px-8 py-4 rounded-xl font-sans font-semibold hover:bg-red-50 hover:text-red-700 transition-all duration-300 flex items-center justify-center gap-3 btn-hover shadow-soft border border-gray-200"
        >
          <SafeIcon icon={FiTrash2} className="text-xl" />
          <span>Clear My Data</span>
        </button>
      </motion.div>

      {/* Encouragement */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.5 }}
        className="text-center py-12 px-8 bg-gradient-to-r from-blue-50 via-orange-50 to-blue-50 rounded-2xl shadow-soft paper-texture"
      >
        <SafeIcon icon={FiHeart} className="text-4xl text-accent mx-auto mb-6" />
        <p className="font-serif text-xl text-gray-700 italic leading-relaxed max-w-2xl mx-auto">
          "Lord, help me see my work clearly — not through pride or fear, but through Your eyes of purpose and possibility."
        </p>
      </motion.div>
    </div>
  );
};

export default Dashboard;